'use client';

import { SubmitHandler, useForm } from 'react-hook-form';
import Input from '../../shared/Input/Input';
import { cn } from 'utils/styles';
import Button from '../../shared/Buttons/BaseButton';
import EmailCapture from '../../shared/EmailCapture/EmailCapture';
import { useEffect, useState } from 'react';
import { CancelCircle, CheckmarkCircle } from '@1debit/icons';
import { useSearchParams } from 'next/navigation';
import { LEAD_GENERATION_INTERACTION_TYPES } from 'components/analytics/types';
import { useAnalyticsModuleTracking } from 'components/analytics/AnalyticsModuleTracking';
import { useLeadGenerationInteraction } from 'components/analytics/hooks';
import { convertObjectToArray } from 'components/analytics/utils';
import { HeroConsolidatedProps } from '../types';

import { liveUpdateAttrs } from 'libs/contentful/utils';

export interface PromoCodeInputProps extends Pick<HeroConsolidatedProps, 'sys'> {
  allowedCodes?: string[];
  promoButtonText: string;
  emailButtonText: string;
  baseEnrollmentUrl: string;
}

interface PromoCodeFormData {
  promoCode: string;
}

const PromoCodeInput = ({
  allowedCodes,
  promoButtonText,
  emailButtonText,
  sys,
  baseEnrollmentUrl,
}: PromoCodeInputProps) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PromoCodeFormData>({ defaultValues: { promoCode: '' } });
  const [submittedCode, setSubmittedCode] = useState('');
  const [codeAppliedSuccessfully, setCodeAppliedSuccessfully] = useState(false);
  const searchParams = useSearchParams();
  const { attributes } = useAnalyticsModuleTracking();

  const analyticsProps = {
    interaction_type: LEAD_GENERATION_INTERACTION_TYPES.PROMO_CODE_ENTERED,
    interaction_details: {
      location: attributes.moduleLocation,
      action_detail: '',
    },
  };

  const trackPromoCodeEntered = useLeadGenerationInteraction(analyticsProps);

  const sendSegmentTrackEvent = (code: string, status: string) => {
    trackPromoCodeEntered({
      interaction_details: { ...analyticsProps.interaction_details, action_detail: code },
      additional_data: convertObjectToArray({ status }),
    });
  };

  const validateCode = (code: string) => {
    if (!allowedCodes || !allowedCodes.length) return false;

    const validCodesList = new RegExp(`\\b(${allowedCodes.join('|')})\\b`, 'i');
    const isValid = validCodesList.test(code);

    if (!isValid) {
      sendSegmentTrackEvent(code, 'Invalid');
    }
    return isValid;
  };

  const applyValidCode = (code: string) => {
    setSubmittedCode(code);
    sendSegmentTrackEvent(code, 'Valid');
    setCodeAppliedSuccessfully(true);
  };

  const removePromoCode = () => {
    reset();
    setSubmittedCode('');
    setCodeAppliedSuccessfully(false);
  };

  const handleCodeFromQueryString = () => {
    const urlPromoCode = searchParams.get('promo_code');
    if (!urlPromoCode) return;

    if (validateCode(urlPromoCode)) {
      applyValidCode(urlPromoCode);
    }
  };

  const onSubmit: SubmitHandler<PromoCodeFormData> = data => {
    applyValidCode(data.promoCode);
  };

  useEffect(() => {
    handleCodeFromQueryString();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (codeAppliedSuccessfully) {
    return (
      <div className="space-y-2">
        <div className="px-4 bg-lettuce text-kale font-sans font-medium rounded-md flex flex-row justify-between items-center">
          <div className="flex flex-row gap-2 items-center">
            <CheckmarkCircle />
            <div>
              <span className="uppercase">{submittedCode}</span> saved
            </div>
          </div>

          <div>
            <Button variant="icon" label="Remove Promo Code" onClick={removePromoCode}>
              <CancelCircle />
            </Button>
          </div>
        </div>
        <EmailCapture
          buttonText={emailButtonText}
          appliedPromoCode={submittedCode}
          baseEnrollmentUrl={baseEnrollmentUrl}
        />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="">
      <div className={cn('md:flex-row flex-col flex items-end gap-2.5 mb-0 h-full')}>
        <Input
          label="Got a promo code?"
          {...register('promoCode', { required: true, validate: validateCode })}
          error={'Enter a valid promo code'}
          invalid={!!errors.promoCode}
        />
        <Button {...liveUpdateAttrs(sys?.id, 'promoCodeInputButtonText')}>{promoButtonText}</Button>
      </div>
    </form>
  );
};

export default PromoCodeInput;
